import React, { useEffect, useState } from 'react';
import Modal from '../../components/components/modal/modal.jsx';
import ApiService from '../../components/api_services/apiService.jsx';
import { showAlert } from '../../components/components/global_alert/alertBus';
import {
    formatDateForInput,
    formatPromotionDate,
    UI_PLAN_DURATION_DAYS,
    getPromotionStartDateBounds,
    validatePromotionStartDate,
    getPromotionTruncationNotice,
    getApiErrorMessage,
    toPromotionStartDateIso,
    isDateWithinPromotionStartBounds,
} from './promoUtils.js';
import PromotionStartDatePicker from './PromotionStartDatePicker.jsx';
import './SchedulePromotionModal.css';

const resolveEventPayload = (response) => {
    const payload = response?.data ?? response;
    return payload?.id != null ? payload : null;
};

function SchedulePromotionModal({ isOpen, promotion, onClose, onSuccess }) {
    const [startDate, setStartDate] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [event, setEvent] = useState(null);
    const [isEventLoading, setIsEventLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setStartDate('');
            setEvent(null);
            setIsSubmitting(false);
            setIsEventLoading(false);
            return;
        }

        if (!promotion) {
            return;
        }

        setStartDate(formatDateForInput(promotion.startsAt) || '');
    }, [isOpen, promotion]);

    useEffect(() => {
        if (!isOpen || !promotion?.eventId) {
            return;
        }

        const loadEvent = async () => {
            setIsEventLoading(true);
            try {
                const response = await ApiService.getEvent(promotion.eventId);
                setEvent(resolveEventPayload(response));
            } catch (error) {
                setEvent(null);
            } finally {
                setIsEventLoading(false);
            }
        };

        loadEvent();
    }, [isOpen, promotion?.eventId]);

    useEffect(() => {
        if (!startDate || !event) {
            return;
        }
        const { min, max } = getPromotionStartDateBounds(event);
        if (!isDateWithinPromotionStartBounds(startDate, min, max)) {
            setStartDate('');
        }
    }, [event, startDate]);

    const { min: minStartDate, max: maxStartDate } = getPromotionStartDateBounds(event);
    const durationDays = UI_PLAN_DURATION_DAYS[promotion?.promotionType]
        || UI_PLAN_DURATION_DAYS[promotion?.planDisplayName]
        || null;
    const truncationNotice = getPromotionTruncationNotice(startDate, durationDays, event?.endDate);

    const handleSubmit = async (submitEvent) => {
        submitEvent.preventDefault();
        if (!promotion?.id || !startDate) {
            showAlert('Wybierz datę rozpoczęcia promowania.');
            return;
        }

        const dateValidationError = validatePromotionStartDate(startDate, event, durationDays);
        if (dateValidationError) {
            showAlert(dateValidationError);
            return;
        }

        const startDateIso = toPromotionStartDateIso(startDate);
        if (!startDateIso) {
            showAlert('Podaj prawidłową datę rozpoczęcia promowania.');
            return;
        }

        setIsSubmitting(true);
        try {
            await ApiService.schedulePromotion(promotion.id, startDateIso);
            showAlert(`Data startu promowania została zmieniona na ${formatPromotionDate(startDateIso)}.`);
            onSuccess?.();
            onClose?.();
        } catch (error) {
            showAlert(getApiErrorMessage(error, 'Nie udało się zmienić daty startu promowania.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="modal-content-2 schedule-promo-modal">
            <h2>Zmień datę startu promowania</h2>
            {promotion?.eventName && (
                <p className="schedule-promo-modal-event">{promotion.eventName}</p>
            )}
            <form onSubmit={handleSubmit} className="schedule-promo-modal-form">
                <label htmlFor="schedule-promo-start-date">Data rozpoczęcia promowania</label>
                <PromotionStartDatePicker
                    value={startDate}
                    onChange={setStartDate}
                    minDate={minStartDate}
                    maxDate={maxStartDate}
                    durationDays={durationDays}
                    eventEndDate={event?.endDate}
                />
                {isEventLoading && (
                    <p className="schedule-promo-modal-hint">Ładowanie danych wydarzenia...</p>
                )}
                {!isEventLoading && event && (
                    <p className="schedule-promo-modal-hint">
                        Start od dziś. Promowanie kończy się najpóźniej wraz z końcem wydarzenia.
                        {durationDays ? ` Pakiet obejmuje ${durationDays} dni promowania.` : ''}
                    </p>
                )}
                {truncationNotice && (
                    <p className="schedule-promo-modal-hint schedule-promo-modal-hint--warn">
                        {truncationNotice}
                    </p>
                )}
                {!isEventLoading && !event && (
                    <p className="schedule-promo-modal-hint schedule-promo-modal-hint--warn">
                        Nie udało się pobrać dat wydarzenia. Zakres zostanie zweryfikowany po stronie serwera.
                    </p>
                )}
                <div className="schedule-promo-modal-actions">
                    <button type="button" className="schedule-promo-modal-cancel" onClick={onClose}>
                        Anuluj
                    </button>
                    <button type="submit" className="schedule-promo-modal-submit" disabled={isSubmitting || isEventLoading}>
                        {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

export default SchedulePromotionModal;
